import React, { useMemo, useState, useEffect } from 'react';
import { Checkbox, Collapse, Select, Button } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import intl from 'react-intl-universal';

import { DateType } from '@/utils/date';
import ReserveRoomItem from '@/components/Reservation/RoomItem';
import { RoomCategory, RoomItem, SelectRoomItem } from '@/redux/reducers/reservation/base.d';
import { flatArrByKey, getRoomCheckInStatus } from '@/redux/actions/reservation/helper';

import styles from './index.module.scss';
import { getPopupContainer } from '../../helper';

const { Panel } = Collapse;

const createOptions: (
  count: number,
) => Array<{
  label: string;
  value: number;
}> = (count) => {
  const result = [];
  for (let targetCount = 1; targetCount <= count; ) {
    result.push({
      label: intl.get('drawer_checkin_room_assign_count_option', { count: targetCount }),
      value: targetCount,
    });
    targetCount += 1;
  }
  return result;
};

const CollapseHeader: React.FC<{
  remainCount: number;
  onSelect: (val: number) => void;
}> = (props) => {
  const [selectValue, setValue] = useState<number | undefined>(1);
  const { remainCount, onSelect } = props;
  const handleSelect = (val?) => {
    setValue(val);
  };
  const handleClick = () => {
    if (selectValue) {
      onSelect(selectValue);
    }
  };
  const isDisabled = remainCount <= 0;
  useEffect(() => {
    if (remainCount < selectValue) {
      setValue(1);
    }
  }, [remainCount]);
  return (
    <div className={styles.collapseItemHeader}>
      <Select
        size="small"
        className={styles.itemCountSelector}
        disabled={isDisabled}
        options={createOptions(remainCount)}
        value={isDisabled ? undefined : selectValue}
        onChange={handleSelect}
        placeholder={intl.get('drawer_checkin_room_assign_count_option', { count: 0 })}
        getPopupContainer={getPopupContainer}
      />
      <Button size="small" type="primary" ghost disabled={isDisabled} onClick={handleClick}>
        {intl.get('reservation_field_room_add_btn')}
      </Button>
    </div>
  );
};

interface IProps {
  isCategoryDisabled: boolean;
  defaultOpenKey: number[];
  selectRoomList: SelectRoomItem[];
  dataSource: RoomCategory[];
  onItemSelect: (item: RoomItem, checked: boolean) => void;
  onCategorySelect: (item: RoomCategory, checked: boolean) => void;
  onBlockSelect: (item: RoomCategory, count: number) => void;
  businessDate: DateType;
}

const Index: React.FC<IProps> = (props) => {
  const {
    isCategoryDisabled,
    defaultOpenKey,
    selectRoomList,
    dataSource,
    businessDate,
    onCategorySelect,
    onItemSelect,
    onBlockSelect,
  } = props;

  // 已选择的房间数据
  const selectData = useMemo(() => {
    const checkedRoomMap: { [key: number]: SelectRoomItem[] } = {}; // 已选房
    if (selectRoomList?.length) {
      selectRoomList.forEach((item) => {
        const { roomTypeId } = item;
        const roomArr = checkedRoomMap[roomTypeId] || [];
        roomArr.push(item);
        checkedRoomMap[roomTypeId] = roomArr;
      });
    }
    return {
      checkedRoomMap,
    };
  }, [selectRoomList]);

  const preventCollapseEvent: React.MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation();
  };

  const handleAllSelect: (item: RoomCategory, event: CheckboxChangeEvent) => void = (
    item,
    event,
  ) => {
    event.stopPropagation();
    onCategorySelect(item, event.target.checked);
  };

  const toggleItemStatus: (item: RoomItem, event: CheckboxChangeEvent) => void = (item, event) => {
    onItemSelect(item, event.target.checked);
  };

  // 自动排房
  const addRoomByType: (val: number, categoryItem: RoomCategory) => void = (val, categoryItem) => {
    if (val <= 0) return;
    onBlockSelect(categoryItem, val);
  };

  const renderRoomList: (
    renderList: RoomItem[],
    selectList: SelectRoomItem[],
  ) => {
    listNodeContent: React.ReactNode;
    listCheckedCount: number;
    listValidCount: number;
  } = (renderList, selectList) => {
    const selectIdMap = flatArrByKey(selectList, 'roomId');
    let checkedCount = 0; // 选中房间数量
    let totalValidCount = 0; // 总可入住房间数量
    const nodeContent = (
      <ul className={styles.collapseItemBody}>
        {renderList.map((item) => {
          const isItemChecked = !!selectIdMap[item.id];
          if (isItemChecked) {
            checkedCount += 1;
          }
          const isValid = getRoomCheckInStatus(item);
          if (isValid) {
            totalValidCount += 1;
          }
          return (
            <li className={styles.roomItem} key={item.id}>
              <Checkbox
                className={styles.roomItemCheckbox}
                checked={isItemChecked}
                disabled={!isValid}
                onChange={(event) => toggleItemStatus(item, event)}
              >
                <ReserveRoomItem data={item} showCalendar businessDate={businessDate} />
              </Checkbox>
            </li>
          );
        })}
      </ul>
    );
    return {
      listNodeContent: nodeContent,
      listCheckedCount: checkedCount,
      listValidCount: totalValidCount,
    };
  };

  const renderPanelContent: (categoryData: RoomCategory) => React.ReactNode = (categoryData) => {
    const { id, name, roomsTotal, roomList } = categoryData;
    const { checkedRoomMap } = selectData;
    const checkedRoomArr = checkedRoomMap[id] || []; // 当前房型下已选房
    const subRoomList = roomList || [];
    const subRoomCount = subRoomList.length;
    const { listNodeContent, listCheckedCount, listValidCount } = renderRoomList(
      subRoomList,
      checkedRoomArr,
    );
    // 是否全选：有可用房间 且选中数量大于等于可用数量
    const isAllChecked = listValidCount && listCheckedCount >= listValidCount;
    const isPartialChecked = listCheckedCount && !isAllChecked; // 部分选中：有选中 且不是全选
    // 自动排房选项数量上限 总房间数-已选房间数量
    const blockedLimitCount = listValidCount - listCheckedCount;
    return (
      <Panel
        header={
          <span onClick={preventCollapseEvent}>
            <Checkbox
              disabled={listValidCount === 0 || isCategoryDisabled}
              indeterminate={isPartialChecked}
              checked={isAllChecked}
              onChange={(event) => handleAllSelect(categoryData, event)}
            >
              {name}
            </Checkbox>
          </span>
        }
        key={id}
        collapsible={subRoomCount === 0 ? 'disabled' : undefined}
        extra={<span className={styles.countIndicator}>{`${listValidCount}/${roomsTotal}`}</span>}
        className={styles.collapseItem}
      >
        {isCategoryDisabled ? null : (
          <CollapseHeader
            remainCount={blockedLimitCount}
            onSelect={(val) => addRoomByType(val, categoryData)}
          />
        )}
        {listNodeContent}
      </Panel>
    );
  };

  if (!dataSource.length) return null;

  return (
    <div className={styles.collapseWrapper}>
      <Collapse
        expandIconPosition="end"
        defaultActiveKey={defaultOpenKey}
        className={styles.mainCollapse}
      >
        {dataSource.map(renderPanelContent)}
      </Collapse>
    </div>
  );
};

export default Index;
